import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { HiCheck, HiChevronRight } from 'react-icons/hi';

import { EXTERNAL_LINKS } from '../../constants/links';
import { registerWithEmail } from '../../api/auth';

const PRIMARY = '#6B6BE8';

const Checkbox = ({ checked, onChange }) => (
    <CheckboxBox $checked={checked} onClick={onChange}>
        {checked && <HiCheck size={13} color="#fff" strokeWidth={1} />}
    </CheckboxBox>
);

const AuthRegisterTermsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { email, password } = location.state ?? {};

    const [isLoading, setIsLoading] = useState(false);
    const [agreeAge, setAgreeAge] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [agreePrivacy, setAgreePrivacy] = useState(false);
    const [agreeMarketing, setAgreeMarketing] = useState(false);
    const [agreePush, setAgreePush] = useState(false);
    const [agreeSms, setAgreeSms] = useState(false);
    const [agreeEmail, setAgreeEmail] = useState(false);

    const allChecked = agreeAge && agreeTerms && agreePrivacy && agreeMarketing && agreePush && agreeSms && agreeEmail;
    const isActive = agreeAge && agreeTerms && agreePrivacy;

    useEffect(() => {
        setAgreeMarketing(agreePush && agreeSms && agreeEmail);
    }, [agreePush, agreeSms, agreeEmail]);

    const handleAgreeAll = () => {
        const next = !allChecked;
        setAgreeAge(next);
        setAgreeTerms(next);
        setAgreePrivacy(next);
        setAgreeMarketing(next);
        setAgreePush(next);
        setAgreeSms(next);
        setAgreeEmail(next);
    };

    const handleAgreeMarketing = () => {
        const next = !agreeMarketing;
        setAgreeMarketing(next);
        setAgreePush(next);
        setAgreeSms(next);
        setAgreeEmail(next);
    };

    return (
        <div style={{ width: '100dvw', height: '100dvh', display: 'flex' }}>
            <BackgroundLayout>
                <FormWrapper>
                    <Title>약관에 동의해주세요</Title>

                    <AgreeAllBox $checked={allChecked} onClick={handleAgreeAll}>
                        <Checkbox checked={allChecked} onChange={handleAgreeAll} />
                        <AgreeAllLabel>아래 내용 모두 동의하기</AgreeAllLabel>
                    </AgreeAllBox>

                    <ItemList>
                        <TermItem>
                            <Checkbox checked={agreeAge} onChange={() => setAgreeAge((v) => !v)} />
                            <ItemLabel>(필수) 만 14세 이상 이용 가능</ItemLabel>
                        </TermItem>

                        <TermItem>
                            <Checkbox checked={agreeTerms} onChange={() => setAgreeTerms((v) => !v)} />
                            <ItemLabel>(필수) 이용약관</ItemLabel>
                            <ChevronButton onClick={() => window.open(EXTERNAL_LINKS.TERMS, '_blank', 'noopener,noreferrer')}>
                                <HiChevronRight size={18} color="#AAAAAA" />
                            </ChevronButton>
                        </TermItem>

                        <TermItem>
                            <Checkbox checked={agreePrivacy} onChange={() => setAgreePrivacy((v) => !v)} />
                            <ItemLabel>(필수) 개인정보 수집 및 이용</ItemLabel>
                            <ChevronButton onClick={() => window.open(EXTERNAL_LINKS.PRIVACY, '_blank', 'noopener,noreferrer')}>
                                <HiChevronRight size={18} color="#AAAAAA" />
                            </ChevronButton>
                        </TermItem>

                        <TermItem>
                            <Checkbox checked={agreeMarketing} onChange={handleAgreeMarketing} />
                            <ItemLabel>(선택) 이벤트/서비스 혜택 알림</ItemLabel>
                            <ChevronButton>
                                <HiChevronRight size={18} color="#AAAAAA" />
                            </ChevronButton>
                        </TermItem>

                        <SubItemRow>
                            <SubItem>
                                <Checkbox checked={agreePush} onChange={() => setAgreePush((v) => !v)} />
                                <SubLabel>푸시 알림</SubLabel>
                            </SubItem>
                            <SubItem>
                                <Checkbox checked={agreeSms} onChange={() => setAgreeSms((v) => !v)} />
                                <SubLabel>SMS/SNS</SubLabel>
                            </SubItem>
                            <SubItem>
                                <Checkbox checked={agreeEmail} onChange={() => setAgreeEmail((v) => !v)} />
                                <SubLabel>이메일</SubLabel>
                            </SubItem>
                        </SubItemRow>
                    </ItemList>

                    <SubmitButton $active={isActive && !isLoading} onClick={async () => {
                        if (!isActive || isLoading) return;
                        setIsLoading(true);
                        try {
                            await registerWithEmail({ email, password });
                            navigate('/');
                        } catch (e) {
                            alert(e.response?.data?.message ?? '회원가입에 실패했습니다.');
                        } finally {
                            setIsLoading(false);
                        }
                    }}>
                        {isLoading ? '처리 중...' : '완료'}
                    </SubmitButton>
                </FormWrapper>
            </BackgroundLayout>
        </div>
    );
};

const BackgroundLayout = styled.div`
    flex: 1;
    background: #F5F6F8;
    display: flex;
    align-items: center;
    justify-content: center;

    @media ${({ theme }) => theme.tablet} {
        background: #FFFFFF;
        padding: 40px;
    }

    @media ${({ theme }) => theme.mobile} {
        background: #FFFFFF;
        padding: 20px;
    }
`;

const FormWrapper = styled.div`
    width: 480px;
    padding: 40px;
    border-radius: 16px;
    border: 1px solid #E5E8EB;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
    background: #FFFFFF;
    display: flex;
    flex-direction: column;
    align-items: stretch;

    @media ${({ theme }) => theme.tablet} {
        padding: 0px;
        box-shadow: none;
        border: none;
        width: 100%;
    }
`;

const Title = styled.h1`
    font-family: Pretendard;
    font-size: 24px;
    font-weight: 700;
    color: #232323;
    text-align: center;
    margin: 0 0 28px;
    letter-spacing: -0.5px;
`;

const AgreeAllBox = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    border-radius: 12px;
    border: 1.5px solid ${({ $checked }) => ($checked ? PRIMARY : '#D1D5DB')};
    background: ${({ $checked }) => ($checked ? '#EFEFFD' : '#FAFAFA')};
    cursor: pointer;
    margin-bottom: 20px;
    transition: all 0.15s;
`;

const AgreeAllLabel = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    color: #232323;
    letter-spacing: -0.3px;
`;

const ItemList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 28px;
`;

const TermItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
`;

const ItemLabel = styled.span`
    flex: 1;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #444444;
    letter-spacing: -0.3px;
`;

const ChevronButton = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

const SubItemRow = styled.div`
    display: flex;
    gap: 24px;
    padding-left: 4px;
`;

const SubItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
`;

const SubLabel = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #666666;
    letter-spacing: -0.3px;
`;

const CheckboxBox = styled.div`
    width: 22px;
    height: 22px;
    min-width: 22px;
    border-radius: 6px;
    border: 1.5px solid ${({ $checked }) => ($checked ? PRIMARY : '#D1D5DB')};
    background: ${({ $checked }) => ($checked ? PRIMARY : '#FFFFFF')};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s;
`;

const SubmitButton = styled.button`
    height: 56px;
    border-radius: 12px;
    border: none;
    background: ${({ $active }) => ($active ? PRIMARY : '#D1D5DB')};
    color: #FFFFFF;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.6px;
    cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
    transition: background 0.15s;
`;

export default AuthRegisterTermsPage;
